import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { Acao, Recurso } from '@arena/shared/permissoes';
import { authApi, type Eu } from '../api/auth.js';
import { quandoPerderSessao, tokens } from '../api/cliente.js';

/**
 * A sessão corrente e o que ela libera.
 *
 * >>> A VERIFICAÇÃO DE PERMISSÃO AQUI É COSMÉTICA. <<<
 *
 * Isto não é ressalva de rodapé, é a regra que sustenta a arquitetura.
 * O `pode()` abaixo serve para ESCONDER o que não interessa — item de
 * menu, botão, coluna. Ele NÃO protege nada: qualquer pessoa abre o
 * console e chama a rota direto.
 *
 * Quem decide é o servidor, sempre, no guard do documento 03. Se algum
 * dia uma tela depender só desta função para impedir alguma coisa, o
 * dado já está exposto — e a correção é no backend, não aqui.
 *
 * A matriz vem de `GET /auth/eu`, ou seja, da tabela `permissoes`
 * daquela arena — não do catálogo estático de `@arena/shared`. É o que
 * faz uma célula editada em #v-acessos mudar o menu sem deploy.
 */

interface Sessao {
  readonly eu: Eu | undefined;
  readonly carregando: boolean;
  /** COSMÉTICO. Ver o comentário acima. */
  readonly pode: (recurso: Recurso, acao: Acao) => boolean;
  readonly entrou: (eu: Eu) => void;
  readonly sair: () => Promise<void>;
  readonly recarregar: () => Promise<void>;
}

const Contexto = createContext<Sessao | undefined>(undefined);

export function useSessao(): Sessao {
  const sessao = useContext(Contexto);
  if (!sessao) throw new Error('useSessao precisa de <ProvedorDeSessao> acima na árvore.');
  return sessao;
}

export function ProvedorDeSessao({ children }: { children: ReactNode }) {
  const [eu, setEu] = useState<Eu | undefined>();
  const [carregando, setCarregando] = useState(true);

  const carregar = useCallback(async () => {
    if (!tokens.ler().acesso) {
      setEu(undefined);
      setCarregando(false);
      return;
    }
    try {
      setEu(await authApi.eu());
    } catch {
      // Token velho ou revogado. O cliente já tentou renovar; se
      // chegou aqui, não há sessão.
      tokens.limpar();
      setEu(undefined);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void carregar();
    // O cliente avisa quando a renovação falha de vez — daí a sessão
    // cai mesmo que nenhuma tela tenha perguntado.
    quandoPerderSessao(() => setEu(undefined));
  }, [carregar]);

  const valor = useMemo<Sessao>(
    () => ({
      eu,
      carregando,
      pode: (recurso, acao) => Boolean(eu?.permissoes[recurso]?.acoes.includes(acao)),
      entrou: (novo) => {
        setEu(novo);
        setCarregando(false);
      },
      sair: async () => {
        await authApi.sair();
        setEu(undefined);
      },
      recarregar: carregar,
    }),
    [eu, carregando, carregar],
  );

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}
